import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  Building2,
  ChefHat,
  Droplets,
  Home,
  Leaf,
  Sun,
  Wallet,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
} from "recharts";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import {
  useWallet,
  THB_PER_USD,
  USD_PER_TOKEN,
  type TokenSym,
} from "@/hooks/use-wallet";
import {
  YIELD_SCENARIO_ORDER,
  YIELD_SCENARIOS,
  scenarioApyFor,
  type YieldScenario,
} from "@/lib/ecosystem-constants";

/* ────────────────────────────────────────────────────────────────── */
/* Mock live data — swap for oracle / RPC calls when contracts ship  */
/* ────────────────────────────────────────────────────────────────── */

type ViewMode = "physical" | "financial";

type Metric = {
  key: string;
  title: string;
  subtitle: string;
  icon: typeof Sun;
  accent: string; // oklch color
  physical: { value: string; unit: string; delta: string };
  financial: { valueUsd: number; delta: string };
  micro: number[];
  chart: "area" | "bar" | "line";
  detail: string;
};

const METRICS: Metric[] = [
  {
    key: "meal24",
    title: "MEAL24 Ecosystem Velocity",
    subtitle: "SFN Central Kitchen throughput · rolling 30d",
    icon: ChefHat,
    accent: "oklch(0.82 0.18 75)",
    physical: { value: "124,500", unit: "kg Moo Krob produced", delta: "+8.4% w/w" },
    financial: { valueUsd: 412_800, delta: "+6.1% w/w" },
    micro: [12, 14, 13, 18, 22, 19, 26, 24, 30, 28, 33, 36],
    chart: "area",
    detail: "Physical meals processed by the anchor kitchen, feeding MEAL24 burn.",
  },
  {
    key: "solar",
    title: "SolarPando Grid Output",
    subtitle: "Off-grid microgrid + EV charging · 24h",
    icon: Sun,
    accent: "oklch(0.86 0.16 90)",
    physical: { value: "2,148", unit: "kWh · 37 active EV sessions", delta: "+3.2% d/d" },
    financial: { valueUsd: 268_400, delta: "+2.9% d/d" },
    micro: [4, 6, 9, 14, 19, 24, 27, 25, 22, 18, 12, 7],
    chart: "line",
    detail: "Clean generation delivered to kitchens, water treatment, and EV bays.",
  },
  {
    key: "aqua",
    title: "AquaPando Reserves",
    subtitle: "UV-filtered distribution + reservoir level",
    icon: Droplets,
    accent: "oklch(0.8 0.14 230)",
    physical: { value: "18,420 L", unit: "distributed · reservoir 82%", delta: "+1.7% d/d" },
    financial: { valueUsd: 94_600, delta: "+1.4% d/d" },
    micro: [22, 24, 21, 26, 28, 25, 30, 32, 29, 34, 33, 36],
    chart: "bar",
    detail: "Premium AquaP water bottled and piped to kitchen + community offtake.",
  },
  {
    key: "planty",
    title: "PlantyPando Token Backing",
    subtitle: "Secured land + reforestation collateral",
    icon: Leaf,
    accent: "oklch(0.84 0.16 160)",
    physical: { value: "32,000 m²", unit: "long-lease · 1,624 trees geo-tagged", delta: "+4.8% m/m" },
    financial: { valueUsd: 486_200, delta: "+3.6% m/m" },
    micro: [8, 10, 12, 14, 15, 18, 20, 22, 25, 27, 30, 32],
    chart: "area",
    detail: "Physical land + biomass permanently backing PLANTY circulation.",
  },
  {
    key: "village",
    title: "VillagePando Occupancy",
    subtitle: "Smart housing sustained by the loop",
    icon: Home,
    accent: "oklch(0.92 0.05 90)",
    physical: { value: "42 / 60", unit: "units live · food+water+solar", delta: "+2 units w/w" },
    financial: { valueUsd: 312_000, delta: "+5.0% w/w" },
    micro: [6, 8, 10, 12, 15, 18, 22, 26, 30, 34, 38, 42],
    chart: "line",
    detail: "Housing units fully covered by ecosystem-generated resources.",
  },
];

/* ────────────────────────────────────────────────────────────────── */

/**
 * Wallet card metadata — balances themselves come from the global
 * `useWallet()` context so any swap or contribution updates them here in
 * real time (interlocking-gear synchronization).
 */
type WalletTokenMeta = { symbol: string; sym: TokenSym; name: string; accent: string };
const WALLET_TOKEN_META: WalletTokenMeta[] = [
  { symbol: "MEAL2", sym: "MEAL2", name: "Daily Utility Credit", accent: "oklch(0.96 0.07 155)" },
  { symbol: "MEAL4", sym: "MEAL4", name: "Meal Anchor", accent: "oklch(0.97 0.13 120)" },
  { symbol: "VILLAGE", sym: "VILLAGE-PANDO", name: "Governance", accent: "oklch(0.92 0.05 90)" },
  { symbol: "SOLAR", sym: "SOLAR-PANDO", name: "SolarPando", accent: "oklch(0.86 0.16 90)" },
  { symbol: "PLANTY", sym: "PLANTY-PANDO", name: "PlantyPando", accent: "oklch(0.84 0.16 160)" },
  { symbol: "AQUA", sym: "AQUA-PANDO", name: "AquaPando", accent: "oklch(0.8 0.14 230)" },
];

function formatUsd(n: number) {
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}
function formatThb(n: number) {
  return `THB ${Math.round(n * THB_PER_USD).toLocaleString("en-US")}`;
}

/* ────────────────────────────────────────────────────────────────── */

export function RwaLiveDashboard() {
  const { t } = useI18n();
  const localizedMetrics: Metric[] = METRICS.map((m) => ({
    ...m,
    title: t(`vp.rwa.metric.${m.key}.title`),
    subtitle: t(`vp.rwa.metric.${m.key}.subtitle`),
    physical: {
      ...m.physical,
      unit: t(`vp.rwa.metric.${m.key}.unit`),
      delta: t(`vp.rwa.metric.${m.key}.deltaPhysical`),
    },
    financial: {
      ...m.financial,
      delta: t(`vp.rwa.metric.${m.key}.deltaFinancial`),
    },
    detail: t(`vp.rwa.metric.${m.key}.detail`),
  }));
  const [view, setView] = useState<ViewMode>("physical");
  const [scenario, setScenario] = useState<YieldScenario>("base");
  const { connected, connecting, connect, disconnect } = useWallet();

  function handleConnect() {
    if (connected) {
      disconnect();
      return;
    }
    void connect();
  }

  const totalUsd = useMemo(
    () => METRICS.reduce((s, m) => s + m.financial.valueUsd, 0),
    [],
  );

  return (
    <section id="rwa-live" className="mt-16">
      {/* Header row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
            {t("vp.rwa.eyebrow")}
          </span>
          <h2 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">
            {t("vp.rwa.title")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {t("vp.rwa.sub")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <ScenarioToggle scenario={scenario} onChange={setScenario} />
          <ViewToggle view={view} onChange={setView} />
          <ConnectButton
            connected={connected}
            connecting={connecting}
            onClick={handleConnect}
          />
        </div>
      </div>


      {/* Live pulse strip */}
      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-3">
        <LivePulse />
        <StripStat label={t("vp.rwa.strip.tavl.label")} value={view === "physical" ? t("vp.rwa.strip.tavl.physicalValue") : formatUsd(totalUsd)} />
        <StripStat label={t("vp.rwa.strip.chain.label")} value={t("vp.rwa.strip.chain.value")} />
        <StripStat label={t("vp.rwa.strip.latency.label")} value={t("vp.rwa.strip.latency.value")} tone="ok" />
        <StripStat label={t("vp.rwa.strip.uptime.label")} value={t("vp.rwa.strip.uptime.value")} tone="ok" />
      </div>

      {/* Investor profile drawer */}
      <AnimatePresence initial={false}>
        {connected && (
          <motion.div
            key="profile"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 24 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden"
          >
            <InvestorProfile view={view} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Metric grid */}
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {localizedMetrics.map((m) => (
          <MetricCard key={m.key} metric={m} view={view} scenario={scenario} />
        ))}
        <UtilityBridgeCard />
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Sub-components                                                     */
/* ────────────────────────────────────────────────────────────────── */

function ViewToggle({
  view,
  onChange,
}: {
  view: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="tablist"
      aria-label={t("vp.rwa.viewToggleAria")}
      className="relative inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1 font-mono text-[10px] uppercase tracking-[0.16em]"
    >
      {(["physical", "financial"] as ViewMode[]).map((v) => {
        const active = view === v;
        return (
          <button
            key={v}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(v)}
            className={cn(
              "relative z-10 px-3.5 py-1.5 transition-colors",
              active ? "text-black" : "text-foreground/60 hover:text-foreground",
            )}
          >
            {active && (
              <motion.span
                layoutId="view-toggle-pill"
                className="absolute inset-0 -z-10 rounded-full bg-[oklch(0.97_0.13_120)]"
                transition={{ type: "spring", stiffness: 400, damping: 34 }}
              />
            )}
            {v === "physical" ? t("vp.rwa.toggle.physical") : t("vp.rwa.toggle.financial")}
          </button>
        );
      })}
    </div>
  );
}

function ScenarioToggle({
  scenario,
  onChange,
}: {
  scenario: YieldScenario;
  onChange: (s: YieldScenario) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Yield scenario"
      className="relative inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1 font-mono text-[10px] uppercase tracking-[0.16em]"
    >
      <span className="pl-2.5 pr-2 text-muted-foreground">Scenario</span>
      {YIELD_SCENARIO_ORDER.map((s) => {
        const active = scenario === s;
        const meta = YIELD_SCENARIOS[s];
        return (
          <button
            key={s}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(s)}
            title={`${meta.label} · ${meta.pct}% baseline APY`}
            className={cn(
              "relative z-10 px-3 py-1.5 transition-colors",
              active ? "text-black" : "text-foreground/60 hover:text-foreground",
            )}
          >
            {active && (
              <motion.span
                layoutId="scenario-toggle-pill"
                className="absolute inset-0 -z-10 rounded-full bg-[oklch(0.86_0.16_90)]"
                transition={{ type: "spring", stiffness: 400, damping: 34 }}
              />
            )}
            {meta.label} · {meta.pct}%
          </button>
        );
      })}
    </div>
  );
}



function ConnectButton({
  connected,
  connecting,
  onClick,
}: {
  connected: boolean;
  connecting: boolean;
  onClick: () => void;
}) {
  const { t } = useI18n();
  const { address } = useWallet();
  const short = address
    ? address.includes("…")
      ? address
      : `${address.slice(0, 4)}…${address.slice(-4)}`
    : "";
  return (
    <button
      onClick={onClick}
      disabled={connecting}
      className={cn(
        "group inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all",
        connected
          ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/15"
          : "border-white/15 bg-white/[0.04] text-foreground hover:bg-white/[0.08]",
      )}
    >
      {connecting ? (
        <>
          <motion.span
            className="h-2 w-2 rounded-full bg-[oklch(0.97_0.13_120)]"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
          {t("vp.rwa.connect.connecting")}
        </>
      ) : connected ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5" />
          {short}
        </>
      ) : (
        <>
          <Wallet className="h-3.5 w-3.5" />
          {t("vp.rwa.connect.cta")}
        </>
      )}
    </button>
  );
}

function LivePulse() {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-300">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
      </span>
      {t("vp.rwa.livePulse")}
    </div>
  );
}

function StripStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok";
}) {
  return (
    <div className="flex items-baseline gap-2 font-mono text-[10px] uppercase tracking-[0.16em]">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("text-foreground", tone === "ok" && "text-emerald-300")}>
        {value}
      </span>
    </div>
  );
}

function MetricCard({
  metric,
  view,
  scenario,
}: {
  metric: Metric;
  view: ViewMode;
  scenario: YieldScenario;
}) {
  const Icon = metric.icon;
  const data = metric.micro.map((v, i) => ({ i, v }));
  const value =
    view === "physical"
      ? metric.physical.value
      : formatUsd(metric.financial.valueUsd);
  const unit =
    view === "physical"
      ? metric.physical.unit
      : `${formatThb(metric.financial.valueUsd)} · Thai baht`;
  const delta = view === "physical" ? metric.physical.delta : metric.financial.delta;

  const apyMin = scenarioApyFor(metric.key, "conservative");
  const apyBase = scenarioApyFor(metric.key, "base");
  const apyMax = scenarioApyFor(metric.key, "optimal");
  const apyActive = scenarioApyFor(metric.key, scenario);
  const scenarioLabel = YIELD_SCENARIOS[scenario].label;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-colors hover:bg-white/[0.04]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-40 blur-3xl transition-opacity group-hover:opacity-70"
        style={{ background: metric.accent }}
      />

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <span
            className="grid h-8 w-8 place-items-center rounded-full border border-white/15 bg-white/[0.04]"
            style={{ boxShadow: `0 0 22px ${metric.accent.replace(")", " / 0.35)")}` }}
          >
            <Icon className="h-4 w-4" style={{ color: metric.accent }} />
          </span>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {metric.title}
            </div>
            <div className="mt-0.5 text-[11px] text-foreground/60">{metric.subtitle}</div>
          </div>
        </div>
        <StatusDot />
      </div>

      <div className="mt-5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-3xl font-semibold tracking-tight">{value}</div>
          <div className="mt-1 truncate text-[11px] text-muted-foreground">{unit}</div>
        </div>
        <div className="text-right">
          <div className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-emerald-300">
            <ArrowUpRight className="h-3 w-3" />
            {delta}
          </div>
        </div>
      </div>

      {/* Yield scenario band — Conservative / Base / Optimal */}
      <div
        className="mt-4 rounded-xl border border-white/10 bg-black/25 p-3"
        title={`APY range across scenarios: ${apyMin.toFixed(1)}% / ${apyBase.toFixed(1)}% / ${apyMax.toFixed(1)}%`}
      >
        <div className="flex items-baseline justify-between font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
          <span>APY · {scenarioLabel}</span>
          <span className="text-foreground/80">
            {apyMin.toFixed(1)}% · {apyBase.toFixed(1)}% · {apyMax.toFixed(1)}%
          </span>
        </div>
        <div className="relative mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.05]">
          {/* full range track */}
          <div
            className="absolute inset-y-0 rounded-full"
            style={{
              left: "0%",
              right: "0%",
              background: `linear-gradient(90deg, ${metric.accent.replace(")", " / 0.25)")}, ${metric.accent.replace(")", " / 0.55)")})`,
            }}
          />
          {/* active scenario marker */}
          <div
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/40"
            style={{
              left: `${((apyActive - apyMin) / Math.max(0.001, apyMax - apyMin)) * 100}%`,
              background: metric.accent,
              boxShadow: `0 0 12px ${metric.accent.replace(")", " / 0.9)")}`,
            }}
          />
        </div>
        <div className="mt-1.5 font-display text-lg font-semibold tracking-tight text-foreground">
          {apyActive.toFixed(1)}% <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">projected</span>
        </div>
      </div>



      {/* Micro-chart */}
      <div className="mt-4 h-14 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {metric.chart === "area" ? (
            <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`g-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={metric.accent} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={metric.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke={metric.accent}
                strokeWidth={1.5}
                fill={`url(#g-${metric.key})`}
                isAnimationActive
              />
            </AreaChart>
          ) : metric.chart === "bar" ? (
            <BarChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <Bar dataKey="v" fill={metric.accent} radius={[2, 2, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <Line
                type="monotone"
                dataKey="v"
                stroke={metric.accent}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="mt-3 border-t border-white/5 pt-3 text-[11px] leading-relaxed text-muted-foreground">
        {metric.detail}
      </div>
    </motion.div>
  );
}

function StatusDot() {
  const { t } = useI18n();
  return (
    <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-emerald-300">
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
      </span>
      {t("vp.rwa.metricCard.online")}
    </span>
  );
}

function UtilityBridgeCard() {
  const { t } = useI18n();
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative overflow-hidden rounded-2xl border border-white/15 bg-gradient-to-br from-white/[0.05] to-white/[0.01] p-5 sm:col-span-2 lg:col-span-3"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(50% 60% at 15% 10%, oklch(0.97 0.13 120 / 0.12), transparent 60%), radial-gradient(40% 60% at 90% 90%, oklch(0.96 0.07 155 / 0.10), transparent 60%)",
        }}
      />

      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
            <Activity className="h-3.5 w-3.5" />
            {t("vp.rwa.bridge.eyebrow")}
          </div>
          <h3 className="mt-2 font-display text-2xl font-medium tracking-tight sm:text-3xl">
            {t("vp.rwa.bridge.title")}
          </h3>
          <p className="mt-3 text-sm text-muted-foreground">
            {t("vp.rwa.bridge.body")}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <BridgeToken symbol="MEAL2" role={t("vp.rwa.bridge.role.meal2")} icon={ChefHat} accent="oklch(0.96 0.07 155)" />
          <BridgeToken symbol="MEAL4" role={t("vp.rwa.bridge.role.meal4")} icon={Building2} accent="oklch(0.97 0.13 120)" />
          <BridgeToken symbol="PANDO" role={t("vp.rwa.bridge.role.pando")} icon={Home} accent="oklch(0.92 0.05 90)" />
        </div>
      </div>
    </motion.div>
  );
}

function BridgeToken({
  symbol,
  role,
  icon: Icon,
  accent,
}: {
  symbol: string;
  role: string;
  icon: typeof Sun;
  accent: string;
}) {
  return (
    <div
      className="flex min-w-[104px] flex-col items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-3 text-center"
      style={{ boxShadow: `0 0 24px ${accent.replace(")", " / 0.25)")}` }}
    >
      <Icon className="h-4 w-4" style={{ color: accent }} />
      <div className="font-display text-sm font-semibold tracking-tight">{symbol}</div>
      <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
        {role}
      </div>
    </div>
  );
}

function InvestorProfile({ view }: { view: ViewMode }) {
  const { t } = useI18n();
  const { balances, address } = useWallet();
  const walletTokens = WALLET_TOKEN_META.map((m) => {
    const balance = balances[m.sym] ?? 0;
    const usd = balance * USD_PER_TOKEN[m.sym];
    return { ...m, balance, usd };
  });
  const totalUsd = walletTokens.reduce((s, w) => s + w.usd, 0);
  const tokenNameMap: Record<string, string> = {
    MEAL2: t("vp.rwa.wallet.token.meal2.name"),
    MEAL4: t("vp.rwa.wallet.token.meal4.name"),
    VILLAGE: t("vp.rwa.wallet.token.pando.name"),
    SOLAR: t("vp.rwa.wallet.token.solar.name"),
    PLANTY: t("vp.rwa.wallet.token.planty.name"),
    AQUA: t("vp.rwa.wallet.token.aqua.name"),
  };
  const short = address
    ? address.includes("…")
      ? address
      : `${address.slice(0, 4)}…${address.slice(-4)}`
    : "";
  return (
    <div className="relative overflow-hidden rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.03] p-5">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full blur-3xl"
        style={{ background: "oklch(0.78 0.18 150 / 0.18)" }}
      />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">
            {t("vp.rwa.investor.eyebrow")}
          </div>
          <div className="mt-1 font-display text-xl font-semibold tracking-tight">
            {t("vp.rwa.investor.walletLabel")} {short}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {t("vp.rwa.investor.portfolioLabel")}{" "}
            <span className="text-foreground">
              {view === "physical" ? formatUsd(totalUsd) : formatThb(totalUsd)}
            </span>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          {t("vp.rwa.investor.disclaimer")}
        </div>
      </div>

      <div className="relative mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {walletTokens.map((tok) => (
          <div
            key={tok.symbol}
            className="rounded-xl border border-white/10 bg-black/30 p-3"
            style={{ boxShadow: `0 0 20px ${tok.accent.replace(")", " / 0.18)")}` }}
          >
            <div className="flex items-center justify-between">
              <span
                className="font-display text-sm font-semibold tracking-tight"
                style={{ color: tok.accent }}
              >
                {tok.symbol}
              </span>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_oklch(0.78_0.18_150_/_0.8)]" />
            </div>
            <div className="mt-2 font-mono text-sm text-foreground">
              {tok.balance.toLocaleString("en-US", { maximumFractionDigits: 2 })}
            </div>
            <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {view === "financial" ? formatUsd(tok.usd) : tokenNameMap[tok.symbol] ?? tok.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
